import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { gql, useMutation } from '@apollo/client';
import { compose, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import _ from 'lodash';
import { captureException } from '@playbookux/playbook-web-core';
import { setTestSession } from '../../../core/actions';

import {
  Amount,
  Container,
  Centered,
  CardContainer,
  Header,
  StudyPlatform,
  TextPrimary,
  TextContainer,
  Footer,
  Loader,
  Message,
} from './styles';
import { testTypes } from '../../constants/options';
import { START_STUDY_MUTATION } from '../../../core/graphql/test-sessions';
import client from '../../../core/services/graphql';

const CLUSTER_ERROR_MESSAGE = "Cannot read properties of null (reading 'startStudy')";

const Congratulations = (props) => {
  /** props */
  const { history = {}, onSetTestSession } = props;

  /** states */
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const [startStudyMutation] = useMutation(START_STUDY_MUTATION);
  const {
    location: { state: { study = {} } = {} } = {}, goBack, push,
  } = history || {};

  const {
    id: segmentId = '',
    projectId = '',
    device = '',
    operatingSystem = [],
    testType = '',
    sessionDuration = '',
    testers = [],
    faceRecording = false,
  } = study || {};
  const [tester = {}] = testers || [];
  const { label = '', amount = '' } = testTypes[testType] || {};

  const takeLater = async () => {
    push('/', { study });
  };

  const validateToTakeUnmoderatedStudy = async () => {
    const { data: preventTakeStudyUnmoderated = {} } = await client.getClient().query({
      query: gql`
          query PreventTakeStudyUnmoderated($segmentId: ID!) {
            preventTakeStudyUnmoderated(segmentId: $segmentId)
          }
        `,
      variables: {
        segmentId,
      },
      skip: !segmentId,
    });
    return preventTakeStudyUnmoderated?.preventTakeStudyUnmoderated;
  };

  const takeStudy = async () => {
    try {
      setLoading(true);

      // unmoderated only, prevent users accessing at same time
      if (testType === 'unmoderated') {
        const isBlocked = await validateToTakeUnmoderatedStudy();
        if (isBlocked) {
          throw new Error('You are not allowed to take this study');
        }
      }

      const { data = {} } = await startStudyMutation({
        variables: {
          testerId: tester?.id || null,
          segmentId,
          projectId,
        },
      });

      const studyLink = data?.startStudy?.studyLink;
      const testSession = data?.startStudy?.testSession;

      if (!studyLink || !testSession) {
        throw new Error(`Invalid data value from startStudyMutation - data: ${JSON.stringify(data)}`);
      }

      onSetTestSession(testSession);
      push(studyLink);
    } catch (e) {
      setMessage('This study is no longer available');

      if (e.message === CLUSTER_ERROR_MESSAGE) {
        captureException('START_STUDY_EXCEPTION', `${JSON.stringify(e)} - [Check PLaybookUx API cluster/queue]`);
      }
    } finally {
      setLoading(false);
    }
  };

  const scheduleTime = () => {
    push(`/schedule?segment_id=${segmentId}`, {
      study,
    });
  };

  return (
    <Container>
      <CardContainer>
        <Header>
          <Header.Left>
            <Amount>
              $
              {_.isObject(amount) ? amount[sessionDuration] : amount}
            </Amount>
          </Header.Left>
          <Header.Center>
            <Header.PlatformContainer>
              {operatingSystem.map((os) => (
                <StudyPlatform key={os} platform={device} os={os} />
              ))}
            </Header.PlatformContainer>
            <Header.TestType>
              {label}
              {faceRecording && ' - Face Recording'}
            </Header.TestType>
          </Header.Center>
        </Header>
        <TextContainer>
          {loading && (
            <Centered>
              <Loader data-loader="congratulations-loader" />
            </Centered>
          )}
          {message && (
            <Centered>
              <Message as="h1">{message}</Message>
              <a
                onClick={goBack}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    goBack();
                  }
                }}
                role="link"
                tabIndex={0}
              >
                Go back to Browse Tests
              </a>
            </Centered>
          )}
          {!loading && !message && (
            <>
              <TextPrimary.Bold>Congratulations! You&apos;ve been selected to participate.</TextPrimary.Bold>
              <TextPrimary>
                Studies are first come, first serve. You can take the study later, but we recommend taking it now to ensure a spot.
              </TextPrimary>
              <TextPrimary>
                Clicking &quot;I&apos;ll take this study later&quot; will move the test to the &quot;Browse Tests&quot; tab
              </TextPrimary>
            </>
          )}
        </TextContainer>
        {!message && (
          <Footer>
            {testType === 'moderated' && (
              <>
                <Footer.ButtonSecondary onClick={takeLater}>
                  Schedule time later
                </Footer.ButtonSecondary>
                <Footer.ButtonPrimary onClick={scheduleTime}>
                  Schedule time now
                </Footer.ButtonPrimary>
              </>
            )}
            {testType !== 'moderated' && (
              <>
                <Footer.ButtonSecondary onClick={takeLater}>
                  I&apos;ll take this study later
                </Footer.ButtonSecondary>

                <Footer.ButtonPrimary onClick={takeStudy} disabled={loading}>
                  Take study now - recommended
                </Footer.ButtonPrimary>
              </>
            )}
          </Footer>
        )}
      </CardContainer>
    </Container>
  );
};

Congratulations.propTypes = {
  onSetTestSession: PropTypes.func.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
    replace: PropTypes.func,
    pop: PropTypes.func,
    goBack: PropTypes.func,
    location: PropTypes.shape({
      state: PropTypes.shape({
        study: PropTypes.shape({
          id: PropTypes.string.isRequired,
          projectId: PropTypes.string.isRequired,
          device: PropTypes.string.isRequired,
          operatingSystem: PropTypes.arrayOf(PropTypes.string).isRequired,
          testType: PropTypes.string.isRequired,
          sessionDuration: PropTypes.number,
          welcomeTitle: PropTypes.string.isRequired,
          welcomeContent: PropTypes.string.isRequired,
          participantsLanguage: PropTypes.string.isRequired,
          testers: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.string.isRequired,
            approved: PropTypes.bool.isRequired,
            removed: PropTypes.bool.isRequired,
            status: PropTypes.string.isRequired,
            testSessions: PropTypes.arrayOf(PropTypes.object),
          })),
          faceRecording: PropTypes.bool,
        }).isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
};

const mapDispatchToProps = (dispatch) => bindActionCreators(
  {
    onSetTestSession: setTestSession,
  },
  dispatch,
);

const enhance = compose(
  connect(null, mapDispatchToProps),
  withRouter,
);

export default enhance(Congratulations);
